
import { gridConfig } from '@core/config/grid.config';
import { ArticleGroupedRibbons, Headlines, Data as ArticleData, ArticleExtraContent } from '@root/modules/article/types/article';
import articleScroll from '@root/modules/article/utils/articleScrollHandler';
import getArticleRibbons from '@root/modules/article/utils/getArticleRibbons';

// Article info section
import ArticleInfo from '@root/modules/article/components/video/ArticleInfo.vue';
import ArticleAdultLayer from '@root/modules/article/components/ArticleAdultLayer.vue';
import ArticleRibbons from '@root/modules/article/components/ArticleRibbons.vue';

// Article body components
import ArticleBody from '@root/modules/article/components/video/ArticleBody.vue';
import ArticleInfoSocial from '@root/modules/article/components/info/ArticleInfoSocial.vue';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import getBannerKey from '@root/modules/ads/utils/getBannerKey';

import ArticleTopHeadlinesSection from '@root/modules/article/components/video/ArticleTopHeadlinesSection.vue';
import ArticleLatestHeadlinesSection from '@root/modules/article/components/video/ArticleLatestHeadlinesSection.vue';
import ArticleSimilarHeadlinesSection from '@root/modules/article/components/video/ArticleSimilarHeadlinesSection.vue';

import ArticleExtraContentService from '@root/modules/article/services/ArticleExtraContent.service';

interface Data {
  isMobileView: boolean;
  relatedArticlesSettings: {
    minParagraphsLength: number;
    afterParagraphPosition: number;
    articlesLimit: number;
    articlesInBodyLimit: number;
    endOfArticleBody: boolean;
    disableRelatedArticles: boolean;
  };
  fontSize: number;
}

interface Methods {
  confirmAdult: () => void;
  handleSetFontSize: () => void;
  getKey: (params: Record<string, any>) => string;
}

interface Computed {
  isCrArticle: boolean;
  isAdultArticle: boolean;
  contentCenterClass: Record<string, boolean>;
  articleRibbons: ArticleGroupedRibbons | null;
  disableAds: boolean;
  disabledAdsList: string[];
  adPlacements: Record<string, any>;
  contentAdPlacement: string;
  contentFallbackAdPlacement: string;
  hideTopHeadlines: boolean;
  hideLatestHeadlines: boolean;
  isAdultConfirmed: boolean;
}

interface Props {
  article: ArticleData;
  headlines: Headlines | [];
  isArticleFetching: boolean;
}

export default articleScroll.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleInfo,
    ArticleBody,
    ArticleAdultLayer,
    ArticleRibbons,
    ArticleInfoSocial,
    ArticleTopHeadlinesSection,
    ArticleLatestHeadlinesSection,
    ArticleSimilarHeadlinesSection,
    AdFormBanner,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    headlines: {
      type: Array,
      required: true,
    },
    isArticleFetching: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isMobileView: false,
      relatedArticlesSettings: this.$channelConfig('page').article.component.relatedArticles,
      fontSize: 1,
    };
  },
  computed: {
    isCrArticle() {
      const articles = this.$store.getters['article/getCrArticles'];

      return articles.find((article: number) => article === this.article.id);
    },
    isAdultArticle() {
      return this.article.settings['icon.adult'] || false;
    },
    contentCenterClass() {
      return { 'content-center': this.article.banners.disableAds };
    },
    articleRibbons() {
      const articleExtraContent: ArticleExtraContent = this.$store.getters['article/getArticleExtraContent'];
      if (!(articleExtraContent && articleExtraContent.ribbons_data)) {
        return null;
      }
      const articleRibbons = getArticleRibbons(this.article, articleExtraContent.ribbons_data);
      return articleRibbons;
    },
    disableAds() {
      return this.article.banners.disableAds;
    },
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    adPlacements() {
      const placements: Record<string, any> = this.$store.getters['adForm/getAdsPlacements'];

      return placements || {};
    },
    contentAdPlacement() {
      return (this.adPlacements.content && this.adPlacements.content.mid[0]) || null;
    },
    contentFallbackAdPlacement() {
      return (this.adPlacements.mobilecrarticle && this.adPlacements.mobilecrarticle.mid[0]) || null;
    },
    hideTopHeadlines() {
      return this.article.primaryCategory.parentCategory?.id === 94378737;
    },
    hideLatestHeadlines() {
      return this.article.primaryCategory.parentCategory?.id === 94378737;
    },
    isAdultConfirmed() {
      return this.$store.state.article.isAdultConfirmed;
    },
  },
  async mounted() {
    if (process.client) {
      const screenWidth = window.screen.width;
      const isIpad = (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) as boolean;
      this.isMobileView = screenWidth <= gridConfig.breakpoints.lg || isIpad;
    } else {
      this.isMobileView = this.$ua && (this.$ua.isFromSmartphone() || this.$ua.isFromMobilephone() || this.$ua.isFromTablet());
    }

    const { externalId: channelId } = this.$channelConfig('settings');

    // Get data for article ribbons and headlines for article bottom section
    const articleExtraContentService = new ArticleExtraContentService();
    const articleExtraContent = await articleExtraContentService.fetch({ channelId });
    this.$store.commit('article/setArticleExtraContent', articleExtraContent || null);

    this.$nextTick(() => {
      this.$emit('article-loaded', this.article.id);
    });
  },
  methods: {
    confirmAdult() {
      this.$store.commit('article/setIsAdultConfirmed', true);
    },
    handleSetFontSize() {
      this.fontSize = this.fontSize === 3 ? 1 : this.fontSize + 1;
    },
    getKey({ prefix, index }) {
      return getBannerKey({ prefix, route: this.$route, store: this.$store, index });
    },
  },
});
